import { useState, useEffect } from 'react';
import { App } from '@capacitor/app';
import { isNative, isIOS } from '@/lib/platform';
import { supabase } from '@/integrations/supabase/client';
import { compareSemver } from '@/lib/semver';
import { APP_STORE_URL, PLAY_STORE_URL } from '@/config/appStore';

interface ForceUpdateState {
  updateRequired: boolean;
  updateRecommended: boolean;
  storeUrl: string;
  loading: boolean;
}

export function useForceUpdate(): ForceUpdateState {
  const [state, setState] = useState<ForceUpdateState>({
    updateRequired: false,
    updateRecommended: false,
    storeUrl: isIOS() ? APP_STORE_URL : PLAY_STORE_URL,
    loading: true,
  });

  useEffect(() => {
    if (!isNative()) {
      setState((prev) => ({ ...prev, loading: false }));
      return;
    }

    async function check() {
      try {
        const appInfo = await App.getInfo();
        const currentVersion = appInfo.version; // e.g. "1.0.0"

        const { data, error } = await supabase
          .from('app_config')
          .select('key, value')
          .in('key', ['minimum_app_version', 'recommended_app_version']);

        if (error || !data) {
          setState((prev) => ({ ...prev, loading: false }));
          return;
        }

        const minVersion = data.find((r) => r.key === 'minimum_app_version')?.value ?? '0.0.0';
        const recVersion = data.find((r) => r.key === 'recommended_app_version')?.value ?? '0.0.0';

        const belowMin = compareSemver(currentVersion, minVersion) < 0;
        const belowRec = !belowMin && compareSemver(currentVersion, recVersion) < 0;

        setState({
          updateRequired: belowMin,
          updateRecommended: belowRec,
          storeUrl: isIOS() ? APP_STORE_URL : PLAY_STORE_URL,
          loading: false,
        });
      } catch {
        setState((prev) => ({ ...prev, loading: false }));
      }
    }

    check();
  }, []);

  return state;
}
